<script setup lang="ts">
import Accordion from 'primevue/accordion'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'

const approaches: {
  icon: string
  title: string
  description: string
  items: { title: string; description: string }[] | null
}[] = [
    {
      icon: 'pi pi-book',
      title: 'Thérapie psychodynamique',
      description:
        'Écoute en profondeur du récit de vie, ayant pour but la compréhension de soi et l\'amélioration du quotidien.',
      items: null
    },
    {
      icon: 'pi pi-cog',
      title: 'Thérapie brève',
      description:
        'Agir rapidement et activement sur un problème précis en utilisant des méthodes comme :',
      items: [
        {
          title: 'Les thérapies comportementales',
          description:
            'Un désapprentissage de schémas mentaux inadaptés (phobies, anxiété, troubles alimentaires, addictions, etc.) à l\'aide d\'exercices mentaux et physiques.'
        },
        {
          title: 'Le traitement des traumatismes',
          description:
            'Un travail sur les chocs subis ayant laissé des traces dans le cerveau, à l\'aide de techniques telles que la désensibilisation, l\'exposition prolongée ou encore la relaxation.'
        },
        {
          title: 'Le Training autogène de Schultz',
          description:
            'Une méthode d\'autohypnose visant un mieux-être immédiat à travers la détente corporelle.'
        },
        {
          title: 'Le Rêve Éveillé',
          description:
            'Un travail de visualisation mentale et dynamique. Approche développée par Nicole Fabre (psychanalyste française, 1925-2023) avec laquelle j\'ai longuement travaillé.'
        }
      ]
    }
  ]

const diplomas = [
  'Diplôme de Psychologue Praticien à l\'École de Psychologues Praticiens de Paris — 1987',
  'Diplôme de Psychopathologie clinique à l\'École de Psychologues Praticiens de Paris — 1988',
  'Formation en Ethnopsychiatrie avec le Pr Tobie Nathan — 1994',
  'Formation en clinique de l\'enfant et de l\'adolescent avec le Pr Jeammet — 1995',
  'Formation en clinique de la mère et du nourrisson avec le Pr Cramer — 1997',
  'Formation à la psychothérapie avec Nicole Fabre — 1993 - 2003'
]

const trainingsGiven = [
  "Formation sur les étapes du développement psychologique de l'enfant et de l'adolescent à l'IFPSS-NC — 1998",
  "Formation sur l'approche psychologique des soins palliatifs avec le Dr Barbet en lien avec l'université de Lyon à l'IFPSS-NC — 2011",
  "Formations continues sur l'approche psychologique des pathologies chroniques (diabète, obésité, BPCO) à l'Agence Sanitaire et Sociale de Nouvelle-Calédonie  — 2001 - 2017"
]
</script>

<template>
  <section id="approches" class="relative overflow-hidden py-24 px-6 md:px-12">
    <div class="absolute inset-0 pointer-events-none" aria-hidden="true">
      <div class="dandelion dandelion-2 absolute top-[8%] right-[6%] w-28 h-36 sm:w-36 sm:h-44 rotate-[10deg]" />
      <div
        class="dandelion dandelion-1 absolute top-[45%] left-[3%] w-20 h-28 sm:w-28 sm:h-36 -scale-x-100 rotate-[-8deg]" />
      <div class="dandelion dandelion-3 absolute bottom-[6%] right-[8%] w-24 h-32 sm:w-32 sm:h-40 rotate-[-6deg]" />
    </div>
    <div class="relative max-w-6xl mx-auto flex flex-col gap-12">
      <div class="flex flex-col gap-3 text-center">
        <p class="uppercase tracking-[0.3em] text-sm text-terracotta-600 dark:text-terracotta-400 font-medium">Mon
          parcours</p>
        <h2 class="text-3xl md:text-4xl text-stone-900 dark:text-stone-50">Approches thérapeutiques</h2>
        <p class="text-base md:text-lg text-stone-600 dark:text-stone-400 max-w-2xl mx-auto leading-relaxed">
          À travers mon parcours, je me suis formée à différentes thérapies :
        </p>
      </div>
      <div class="max-w-3xl mx-auto w-full flex flex-col gap-6">
        <div v-for="approach in approaches" :key="approach.title"
          class="card-hover flex flex-col gap-4 p-6 rounded-xl bg-white/70 dark:bg-stone-900/60 border border-stone-200 dark:border-stone-800 backdrop-blur-sm">
          <div class="flex items-center gap-4">
            <div
              class="w-12 h-12 rounded-lg bg-terracotta-100 dark:bg-terracotta-900/40 flex items-center justify-center shrink-0">
              <i :class="approach.icon" class="text-xl text-terracotta-700 dark:text-terracotta-300"
                aria-hidden="true" />
            </div>
            <h3 class="text-lg text-stone-900 dark:text-stone-50">{{ approach.title }}</h3>
          </div>
          <p class="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">{{ approach.description }}</p>
          <ul v-if="approach.items"
            class="flex flex-col gap-2 list-disc pl-5 text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
            <li v-for="item in approach.items" :key="item.title">
              <span class="font-semibold text-stone-800 dark:text-stone-200">{{ item.title }}</span>
              — {{ item.description }}
            </li>
          </ul>
        </div>
      </div>
      <div class="max-w-3xl mx-auto w-full">
        <Accordion value="0">
          <AccordionPanel value="0">
            <AccordionHeader>Diplômes et formations</AccordionHeader>
            <AccordionContent>
              <ul class="flex flex-col gap-2 list-disc pl-5 text-stone-600 dark:text-stone-300 leading-relaxed">
                <li v-for="item in diplomas" :key="item">{{ item }}</li>
              </ul>
            </AccordionContent>
          </AccordionPanel>
          <AccordionPanel value="1">
            <AccordionHeader>Formations données</AccordionHeader>
            <AccordionContent>
              <ul class="flex flex-col gap-2 list-disc pl-5 text-stone-600 dark:text-stone-300 leading-relaxed">
                <li v-for="item in trainingsGiven" :key="item">{{ item }}</li>
              </ul>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
      </div>
    </div>
  </section>
</template>
